import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { usesMultiIndication } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { styled, Typography } from "@material-ui/core";
import {
  EPOCUS_AMBER_BG,
  EPOCUS_AMBER_BORDER,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { useCan } from "../../_lib/rbac/use-can";
import {
  selectActiveProgram,
  useMyPrograms,
} from "../../_lib/use-active-program";
import { useEnabledExamTypes } from "../../_lib/use-enabled-exam-types";
import { useMyProbeAssociations } from "../../_lib/use-probe-associations";
import { SeedDemoStudiesControls } from "./seed-demo-studies-controls";
import { SettingsCard, SettingsCardLabel } from "./settings-card";
import { useSeedDemoStudies } from "./use-seed-demo-studies";

const DemoCard = styled(SettingsCard)({
  border: `1px dashed ${EPOCUS_AMBER_BORDER}`,
  backgroundColor: EPOCUS_AMBER_BG,
});

const NO_PROBE_MESSAGE =
  "Link a probe above first, so seeded studies reach your queue.";

export const DemoDataCard: FC = () => {
  const { programs } = useMyPrograms();
  const programId = selectActiveProgram(programs)?.id ?? null;
  const { seed, clear, pool, library, loading } = useSeedDemoStudies(programId);
  const enabledExamTypes = useEnabledExamTypes().filter(
    (examType) => !usesMultiIndication(examType),
  );
  const canClear = useCan("clearSeededDemoStudies");
  const { associations } = useMyProbeAssociations();
  const [restricted] = useFeatureFlag(
    featureFlags.epocus.serialRestrictedTriage,
  );

  const mySerial = associations[0]?.deviceSerialNumber ?? null;
  const seedSerial = restricted ? mySerial : null;

  return (
    <DemoCard>
      <SettingsCardLabel>Seed demo studies</SettingsCardLabel>
      <Typography style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}>
        Adds unclaimed studies, with images, to the To document queue on the
        home page.{" "}
        {library
          ? "Images come from the seed library and match the exam types you pick."
          : "Images are copied from studies already in this program."}{" "}
        {seedSerial
          ? "They arrive in your queue."
          : "They arrive in the shared unclaimed pool."}{" "}
        Safe to document, review and delete.
      </Typography>
      <SeedDemoStudiesControls
        pool={pool}
        library={library}
        loading={loading}
        enabledExamTypes={enabledExamTypes}
        blockedReason={restricted && !mySerial ? NO_PROBE_MESSAGE : null}
        canClear={canClear}
        onSeed={(request) => seed(seedSerial, request)}
        onClear={clear}
      />
    </DemoCard>
  );
};
